import { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Card } from "./ui";
import { fmtDate, day } from "./store";

const DOW = ["L", "M", "X", "J", "V", "S", "D"];

export const CalendarView = ({ data }) => {
  const { tasks, leads } = data;
  const [cursor, setCursor] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [selectedDay, setSelectedDay] = useState(day(0));

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const firstDow = (new Date(year, month, 1).getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const eventsFor = (iso) => {
    const ev = [];
    tasks.forEach((t) => { 
      if (t.fechaLimite === iso) {
        const asignado = t.asignadoA ? ` (${t.asignadoA})` : "";
        ev.push({ tipo: "tarea", texto: `${t.titulo}${asignado}` }); 
      }
    });
    leads.forEach((l) => { if (l.fechaProxima === iso) ev.push({ tipo: "seguimiento", texto: `${l.nombre}: ${l.proximaAccion || "seguimiento"}` }); });
    return ev;
  };

  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let dnum = 1; dnum <= daysInMonth; dnum++) {
    const iso = `${year}-${String(month + 1).padStart(2, "0")}-${String(dnum).padStart(2, "0")}`;
    cells.push({ dnum, iso, events: eventsFor(iso) });
  }

  const monthName = cursor.toLocaleDateString("es-ES", { month: "long", year: "numeric" });
  const shift = (n) => setCursor(new Date(year, month + n, 1));
  const selEvents = eventsFor(selectedDay);

  return (
    <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
      <Card className="p-5 xl:col-span-2">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold capitalize text-gray-900">{monthName}</h3>
          <div className="flex gap-2">
            <button data-testid="cal-prev" onClick={() => shift(-1)} className="rounded-lg border border-gray-200 p-1.5 text-gray-500 hover:bg-gray-50" aria-label="Mes anterior"><ChevronLeft className="h-4 w-4" /></button>
            <button data-testid="cal-next" onClick={() => shift(1)} className="rounded-lg border border-gray-200 p-1.5 text-gray-500 hover:bg-gray-50" aria-label="Mes siguiente"><ChevronRight className="h-4 w-4" /></button>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-7 gap-1 text-center text-[10px] font-semibold uppercase tracking-wide text-gray-400">
          {DOW.map((d) => <div key={d} className="py-1">{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {cells.map((c, i) =>
            c === null ? <div key={`e${i}`} /> : (
              <button
                key={c.iso}
                data-testid={`cal-day-${c.iso}`}
                onClick={() => setSelectedDay(c.iso)}
                className={`flex min-h-[64px] flex-col items-center rounded-xl border p-1.5 text-sm transition-colors ${
                  c.iso === selectedDay ? "border-[#2d5a3d] bg-[#2d5a3d]/5" : "border-transparent hover:bg-gray-50"
                } ${c.iso === day(0) ? "font-bold text-[#2d5a3d]" : "text-gray-700"}`}
              >
                <span>{c.dnum}</span>
                <span className="mt-1 flex gap-1">
                  {c.events.slice(0, 4).map((e, j) => (
                    <span key={j} className={`h-1.5 w-1.5 rounded-full ${e.tipo === "tarea" ? "bg-blue-500" : "bg-emerald-500"}`} />
                  ))}
                </span>
              </button>
            )
          )}
        </div>
        <div className="mt-4 flex gap-5 text-xs text-gray-500">
          <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-blue-500" /> Tarea</span>
          <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-emerald-500" /> Seguimiento lead</span>
        </div>
      </Card>

      <Card className="p-5">
        <h4 className="text-sm font-semibold text-gray-900">Eventos del {fmtDate(selectedDay)}</h4>
        <div className="mt-4 space-y-3" data-testid="cal-day-events">
          {selEvents.length === 0 && <p className="text-xs text-gray-400">No hay eventos este día</p>}
          {selEvents.map((e, i) => (
            <div key={i} className="flex items-start gap-2.5 text-sm">
              <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${e.tipo === "tarea" ? "bg-blue-500" : "bg-emerald-500"}`} />
              <div>
                <p className="text-gray-800">{e.texto}</p>
                <p className="text-[11px] uppercase tracking-wide text-gray-400">{e.tipo === "tarea" ? "Tarea" : "Seguimiento"}</p>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};
